// AccesCantera.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axiosConfig';
import { Box, Container, Typography, Stack } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useStatsTheme } from './v2/theme/statsTheme';
import {
  CanteraCard,
  CanteraButton,
  CanteraTextField,
} from './components/shared';

const AccesCantera = () => {
  const { t } = useTranslation();
  const theme = useStatsTheme();
  const [correo, setCorreo] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);
    try {
      const response = await api.post('/api/cantera/recupereAcces', { mail: correo });
      setSuccessMessage(response.data.message || t('accesCantera.successMessage'));
      setCorreo('');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || t('accesCantera.errorMessage'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `radial-gradient(circle at 20% 10%, ${theme.accentSoft} 0%, transparent 50%), radial-gradient(circle at 80% 90%, ${theme.withAlpha(theme.accentSecondary, 0.18)} 0%, transparent 50%), ${theme.surfaces.base}`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Container maxWidth="xs" disableGutters>
        <CanteraCard tone="dark" padding={3} elevation="lg">
          <Stack spacing={2.5} alignItems="center">
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: `${theme.radius.xl}px`,
                bgcolor: theme.surfaces.base,
                border: `1px solid ${theme.surfaces.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 1.5,
              }}
            >
              <img
                src="/assets/valenciaapp.png"
                alt="Valencia App Logo"
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  color: theme.text.primary,
                  fontSize: { xs: 22, sm: 26 },
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                }}
              >
                {t('accesCantera.title')}
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <Stack spacing={1.75}>
                <CanteraTextField
                  label={t('accesCantera.emailLabel')}
                  type="email"
                  fullWidth
                  value={correo}
                  onChange={(e) => setCorreo(e.target.value)}
                  autoComplete="email"
                  required
                />
                <CanteraButton
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={isLoading}
                >
                  {t('accesCantera.submit')}
                </CanteraButton>
              </Stack>
            </Box>

            {error && (
              <Box
                sx={{
                  width: '100%',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  bgcolor: theme.withAlpha(theme.actionColors.missed, 0.12),
                  border: `1px solid ${theme.withAlpha(theme.actionColors.missed, 0.35)}`,
                  borderRadius: `${theme.radius.md}px`,
                }}
              >
                <ErrorOutlineIcon sx={{ color: theme.actionColors.missed, fontSize: 20, mt: 0.2 }} />
                <Typography sx={{ color: theme.text.danger, fontSize: 13, fontWeight: 500 }}>
                  {error}
                </Typography>
              </Box>
            )}

            {successMessage && (
              <Box
                sx={{
                  width: '100%',
                  p: 1.75,
                  bgcolor: theme.withAlpha(theme.actionColors.made, 0.12),
                  border: `1px solid ${theme.withAlpha(theme.actionColors.made, 0.35)}`,
                  borderRadius: `${theme.radius.md}px`,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <CheckCircleOutlineIcon sx={{ color: theme.actionColors.made, fontSize: 20 }} />
                  <Typography sx={{ color: theme.actionColors.made, fontSize: 14, fontWeight: 700 }}>
                    {successMessage}
                  </Typography>
                </Stack>
                <Typography sx={{ color: theme.text.secondary, fontSize: 12, pl: 3.5 }}>
                  {t('accesCantera.checkInbox')}
                </Typography>
              </Box>
            )}
          </Stack>
        </CanteraCard>
      </Container>
    </Box>
  );
};

export default AccesCantera;
